import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import MountainCard from '../components/MountainCard';
import MountainCardSkeleton from '../components/MountainCardSkeleton';
import Filter from '../components/Filter';
import Pagination from '../components/Pagination';

import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import styles from './BrowsePage.module.css';
import { useTranslation } from 'react-i18next';

const getImageLikeCount = (image) => (image && typeof image === 'object' ? image.likes || 0 : 0);

const sortImagesByLikes = (images) => {
  if (!Array.isArray(images) || images.length <= 1) return images;
  return [...images].sort((a, b) => getImageLikeCount(b) - getImageLikeCount(a));
};

function BrowsePage({ mountains, loading, onOpenLogin, onUploadSuccess }) {
  const { currentUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  

  const normalizedMountains = useMemo(() => (
    mountains.map((mountain) => {
      const sortedImages = sortImagesByLikes(mountain.images);
      if (sortedImages === mountain.images) {
        return mountain;
      }
      return { ...mountain, images: sortedImages };
    })
  ), [mountains]);

  const [filter, setFilter] = useState({
    country: '',
    heightCategory: '',
    gearProfile: '',
    mountainRange: '',
    seasonMonth: ''
  });
  const [sortOrder, setSortOrder] = useState('height-desc');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 14;

  const openUploadPopup = () => {
    navigate('/upload');
  };

  const heightCategories = useMemo(() => ({
    baby: { min: 0, max: 3000, label: t('browse.heightLabels.baby') },
    '3000': { min: 3000, max: 4000, label: t('browse.heightLabels.3000') },
    '4000': { min: 4000, max: 5000, label: t('browse.heightLabels.4000') },
    '5000': { min: 5000, max: 6000, label: t('browse.heightLabels.5000') },
    '6000': { min: 6000, max: 7000, label: t('browse.heightLabels.6000') },
    '7000': { min: 7000, max: 8000, label: t('browse.heightLabels.7000') },
    '8000': { min: 8000, max: 9000, label: t('browse.heightLabels.8000') }
  }), [t]);

  const gearProfileLabels = useMemo(() => ({
    hiking: t('browse.gearProfiles.hiking'),
    'easy-alpinism': t('browse.gearProfiles.easy-alpinism'),
    'hard-alpinism': t('browse.gearProfiles.hard-alpinism'),
    himalayan: t('browse.gearProfiles.himalayan')
  }), [t]);

  const sortedMountains = [...normalizedMountains].sort((a, b) => {
    if (sortOrder === 'height-asc') return a.height - b.height;
    return b.height - a.height;
  });

  const filteredMountains = sortedMountains.filter(mountain => {
    if (filter.heightCategory) {
      const category = heightCategories[filter.heightCategory];
      if (mountain.height < category.min || mountain.height >= category.max) return false;
    }
    if (filter.country && mountain.country !== filter.country) return false;
    if (filter.mountainRange && mountain.mountainRange !== filter.mountainRange) return false;
    if (filter.gearProfile && mountain.gearProfile !== filter.gearProfile) return false;
    if (filter.seasonMonth) {
      const monthIndex = { /* monthIndex mapping here if you use it */ };
      const sel = monthIndex[filter.seasonMonth];
      if (sel !== undefined) {
        const sName = mountain.season?.startMonth;
        const eName = mountain.season?.endMonth;
        const s = sName ? monthIndex[sName] : undefined;
        const e = eName ? monthIndex[eName] : undefined;
        if (s === undefined && e === undefined) return false;
        else if (s !== undefined && e !== undefined) {
          if (s <= e) {
            if (!(sel >= s && sel <= e)) return false;
          } else {
            if (!(sel >= s || sel <= e)) return false;
          }
        } else if (s !== undefined) {
          if (sel !== s) return false;
        } else if (e !== undefined) {
          if (sel !== e) return false;
        }
      }
    }
    return true;
  });

  const displayedMountains = filteredMountains;
  const totalPages = Math.ceil(displayedMountains.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMountains = displayedMountains.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFilterChange = (filterType, value) => {
    setFilter(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilter({ country: '', heightCategory: '', gearProfile: '', mountainRange: '', seasonMonth: '' });
    setSortOrder('height-desc');
    setCurrentPage(1);
  };

  const getFilteredTitle = () => {
    const totalCount = normalizedMountains.length;
    const filteredCount = filteredMountains.length;

    if (filteredCount === totalCount) {
      return t('browse.allTitle', { count: totalCount });
    }

    const activeFilters = [];
    if (filter.country) activeFilters.push(filter.country);
    if (filter.seasonMonth) activeFilters.push(filter.seasonMonth);
    if (filter.mountainRange) activeFilters.push(filter.mountainRange);
    if (filter.heightCategory) activeFilters.push(heightCategories[filter.heightCategory].label);
    if (filter.gearProfile) activeFilters.push(gearProfileLabels[filter.gearProfile] || filter.gearProfile);
    if (sortOrder === 'height-desc') activeFilters.push(t('browse.sortHigh'));
    if (sortOrder === 'height-asc') activeFilters.push(t('browse.sortLow'));

    if (activeFilters.length === 0) {
      return t('browse.allRangeTitle', { filtered: filteredCount, total: totalCount });
    }

    return t('browse.filteredTitle', {
      filters: activeFilters.join(', '),
      filtered: filteredCount,
      total: totalCount
    });
  };

  return (
    <>
      <Helmet>
        <title>{t('browse.helmetTitle')}</title>
        <meta name="description" content={t('browse.helmetDescription')} />
      </Helmet>
      <div>
        <div className={styles.resultsHeader}>
          <h2 className={styles.title}>{getFilteredTitle()}</h2>
          <div className={styles.filterWrapper}>
            <Filter
              filter={filter}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              mountains={normalizedMountains}
              heightCategories={heightCategories}
              sortOrder={sortOrder}
              onSortChange={setSortOrder}
            />
          </div>
        </div>
        {loading ? (
          <div className={styles.mountainsGrid}>
            {Array.from({ length: 8 }).map((_, idx) => (
              <MountainCardSkeleton key={idx} />
            ))}
          </div>
        ) : currentMountains.length === 0 ? (
          <div className={styles.emptyState}>
            <h2>{t('browse.emptyTitle')}</h2>
            <p>{t('browse.emptyDescription')}</p>
            {currentUser && (
              <button onClick={openUploadPopup} className={styles.uploadBtn}>
                {t('browse.uploadFirst')}
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mountains grid */}
            <div className={styles.mountainsGrid}>
              {currentMountains.map(mountain => (
                <MountainCard
                  key={mountain.id}
                  mountain={mountain}
                />
              ))}
              {/* Upload card only when not searching and logged in */}
              {currentUser && (
                <div
                  className={`${styles.uploadCard} ${!currentUser ? styles.disabledUploadCard : ''}`}
                  onClick={() => {
                    if (currentUser) {
                      openUploadPopup();
                    } else {
                      onOpenLogin();
                    }
                  }}
                >
                  <div className={styles.uploadCardContent}>
                    <div className={styles.uploadIcon}>+</div>
                    <h3>{t('browse.cardTitle')}</h3>
                    <p>
                      {currentUser ? t('browse.cardBodyLoggedIn') : t('browse.cardBodyLoggedOut')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />

          </>
        )}
      </div>
    </>
  );
}

export default BrowsePage;
